import { useState, useRef, useEffect, useCallback } from "react";
import { Menu, Bell, LogOut, ChevronDown } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "~/stores/auth.store";
import { STORAGE_KEYS } from "~/config/constants";
import { cn } from "~/lib/utils/cn";

interface HeaderProps {
  onToggleSidebar: () => void;
}

export function Header({ onToggleSidebar }: HeaderProps) {
  const { t } = useTranslation();
  const { business, clearAuth } = useAuthStore();

  return (
    <header className="flex h-16 shrink-0 items-center justify-between border-b border-border bg-card px-4 lg:px-6">
      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={onToggleSidebar}
          className="rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground lg:hidden"
          aria-label="Toggle sidebar"
        >
          <Menu className="h-5 w-5" />
        </button>

        {business && (
          <div className="flex items-center gap-2">
            <h1 className="text-lg font-semibold text-foreground">
              {business.name}
            </h1>
            <OperationBadge status={business.operationStatus} />
          </div>
        )}
      </div>

      <div className="flex items-center gap-2">
        <button
          type="button"
          className="relative rounded-md p-2 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          aria-label={t("settings.notifications")}
        >
          <Bell className="h-5 w-5" />
        </button>

        <div className="flex items-center gap-2">
          <LanguageSwitch />
          <button
            type="button"
            onClick={clearAuth}
            className="flex items-center gap-1.5 rounded-md px-3 py-2 text-sm text-muted-foreground hover:bg-accent hover:text-accent-foreground"
          >
            <LogOut className="h-4 w-4" />
            <span className="hidden sm:inline">{t("auth.logout")}</span>
          </button>
        </div>
      </div>
    </header>
  );
}

function OperationBadge({
  status,
}: {
  status: string;
}) {
  const { t } = useTranslation();

  const config: Record<string, { label: string; className: string }> = {
    Regular: {
      label: t("dashboard.regular"),
      className: "bg-green-100 text-green-800",
    },
    ManuallyClosed: {
      label: t("dashboard.manuallyClosed"),
      className: "bg-red-100 text-red-800",
    },
    PausedOrders: {
      label: t("dashboard.pausedOrders"),
      className: "bg-amber-100 text-amber-800",
    },
  };

  const { label, className } = config[status] ?? {
    label: status,
    className: "bg-gray-100 text-gray-800",
  };

  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-medium",
        className,
      )}
    >
      {label}
    </span>
  );
}

function LanguageSwitch() {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const languages = [
    { code: "he", label: "עברית", shortLabel: "עב" },
    { code: "en", label: "English", shortLabel: "EN" },
    { code: "ru", label: "Русский", shortLabel: "РУ" },
    { code: "ar", label: "العربية", shortLabel: "عر" },
  ];

  const current =
    languages.find((l) => l.code === i18n.language) ?? languages[0];

  const handleSelect = useCallback(
    (code: string) => {
      i18n.changeLanguage(code);
      localStorage.setItem(STORAGE_KEYS.LANGUAGE, code);
      setOpen(false);
    },
    [i18n],
  );

  useEffect(() => {
    if (!open) return;

    function handleClickOutside(event: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target as Node)
      ) {
        setOpen(false);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  return (
    <div className="relative" ref={containerRef}>
      <button
        type="button"
        className="flex items-center gap-1 rounded-md px-2 py-1.5 text-sm font-medium text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        aria-haspopup="listbox"
        aria-label={t("settings.language")}
      >
        {current.shortLabel}
        <ChevronDown
          className={cn(
            "h-3 w-3 transition-transform",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div
          role="listbox"
          aria-label={t("settings.language")}
          className="absolute end-0 top-full z-50 mt-1 min-w-[140px] overflow-hidden rounded-lg border border-border bg-card shadow-lg"
        >
          {languages.map((lang) => (
            <button
              key={lang.code}
              type="button"
              role="option"
              aria-selected={i18n.language === lang.code}
              onClick={() => handleSelect(lang.code)}
              className={cn(
                "flex w-full items-center gap-2 px-3 py-2 text-sm transition-colors",
                i18n.language === lang.code
                  ? "bg-primary/10 font-medium text-primary"
                  : "text-foreground hover:bg-accent",
              )}
            >
              <span className="min-w-[24px] font-medium">
                {lang.shortLabel}
              </span>
              <span>{lang.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
